import * as React from 'react';
import { StyledItemInfo, AvatarWrapper, AvatarBorder, ItemText, ItemSubtext, StylingProps, TextContainer, AvatarName } from './PeopleListItem.styles';
import { CheckboxV2, CheckboxV2Props, Flex, TextV2 } from '@withjoy/joykit';
import type { ChangePersonDeliveryMethodHandler } from '@shared/components/PeoplePicker/PeoplePicker.controller';
import { TooltipV2Wrapper } from '../TooltipV2Wrapper';
import { useTranslation } from '@shared/core/i18n';

export type InGroupPosition = 'first' | 'middle' | 'last' | '';

interface SimpleProps
  extends Readonly<{
    avatarUrl?: string;
    labelText: string;
    sublabelText?: string;
    disabled?: boolean;
    inGroupPosition?: InGroupPosition;
    isSmall?: boolean;
    avatarName?: string;
  }> {}

const CHECKBOX_OVERRIDES: CheckboxV2Props['overrides'] = { Control: { props: { _focus: { boxShadow: 'unset' } } } };

const Simple: React.FC<SimpleProps> = React.memo(({ avatarUrl, labelText, sublabelText, disabled, isSmall, inGroupPosition, avatarName, children }) => {
  const isNoNamePlusOne = inGroupPosition !== 'first' && !avatarName;
  const initials = avatarName || (inGroupPosition !== 'first' ? '+1' : '?');
  const subProps: StylingProps = { isSmall, inGroupPosition, disabled, isNoNamePlusOne };
  return (
    <StyledItemInfo {...subProps}>
      <AvatarWrapper>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={labelText + ' photo'}
          />
        ) : (
          <AvatarName>{initials}</AvatarName>
        )}
        {inGroupPosition && <AvatarBorder />}
      </AvatarWrapper>
      <Flex
        flexDirection="column"
        overflow="hidden"
        flex={1}
      >
        <TextContainer>
          <ItemText>{labelText}</ItemText>
          {!isSmall && sublabelText && <ItemSubtext>{sublabelText}</ItemSubtext>}
        </TextContainer>
        {children}
      </Flex>
    </StyledItemInfo>
  );
});

export interface WithDeliveryMethodProps
  extends SimpleProps,
    Readonly<{
      id: string;
      enableSmsSelection: boolean;
      onDeliveryMethodChange: ChangePersonDeliveryMethodHandler;
      email?: string;
      hasPhoneNumber?: boolean;
      hasEmailDeliveryMethodSelected?: boolean;
      hasTextDeliveryMethodSelected?: boolean;
    }> {}

const WithDeliveryMethod: React.FC<WithDeliveryMethodProps> = React.memo(
  ({ id, email, enableSmsSelection, hasPhoneNumber, hasEmailDeliveryMethodSelected, hasTextDeliveryMethodSelected, onDeliveryMethodChange, ...baseProps }) => {
    const { tq } = useTranslation('peoplePicker');

    return (
      <Simple {...baseProps}>
        {enableSmsSelection && (
          <Flex
            columnGap={4}
            marginTop={4}
          >
            <TooltipV2Wrapper
              closeOnScroll
              onMouseEnterDelay={500}
              onMouseLeaveDelay={0}
              disabled={!!hasPhoneNumber}
              content={<TextV2 typographyVariant="label2">{tq`Missing or invalid phone number`}</TextV2>}
            >
              <div>
                <CheckboxV2
                  overrides={CHECKBOX_OVERRIDES}
                  typographyVariant="label2"
                  isDisabled={!hasPhoneNumber}
                  isChecked={!!hasTextDeliveryMethodSelected}
                  onChange={e => {
                    const isChecked = e.target.checked;
                    onDeliveryMethodChange(id, isChecked, 'text');
                  }}
                >
                  {tq`Text`}
                </CheckboxV2>
              </div>
            </TooltipV2Wrapper>
            <TooltipV2Wrapper
              closeOnScroll
              onMouseEnterDelay={500}
              onMouseLeaveDelay={0}
              disabled={!!email}
              content={<TextV2 typographyVariant="label2">{tq`Missing or invalid email address`}</TextV2>}
            >
              <div>
                <CheckboxV2
                  overrides={CHECKBOX_OVERRIDES}
                  typographyVariant="label2"
                  isDisabled={!email}
                  isChecked={!!hasEmailDeliveryMethodSelected}
                  onChange={e => {
                    const isChecked = e.target.checked;
                    onDeliveryMethodChange(id, isChecked, 'email');
                  }}
                >
                  {tq`Email`}
                </CheckboxV2>
              </div>
            </TooltipV2Wrapper>
          </Flex>
        )}
      </Simple>
    );
  },
);

export const PeopleListItem = {
  Simple,
  WithDeliveryMethod,
};
